import { GroupBase } from "react-select";

import { isApiError } from "@config/apiClient";
import ToastService from "@services/toast.service";
import { getDefaultItem } from "@utils/objects.util";
import { addUrlProtocol, getBaseUrl, removeHttpPrefix, validateURL } from "@utils/strings.util";
import { IPagedResponse } from "@interfaces/responses.interface";
import {
  CrossTeamCounterpart,
  CrossTeamLastMatchingDomain,
  ICompany,
  ICompanyPriority,
  ICompanyStage,
  ICompanyType,
} from "@interfaces/company.interface";
import { ICompanyTypeSelectOption, IPrioritySelectOption } from "./Selects";
import { IPipelineStageSelectOption } from "@components/AddNewEntity";
import { DealStage } from "@app-types/common.type";
import { DEFAULT_NO_STAGE_LABEL } from "@hooks/useNoStageLabel";

type FieldErrorBody = {
  website?: string | string[];
  old_website?: string | string[];
  new_website?: string | string[];
  alternative_domains?: string | string[];
  message?: string;
  detail?: string;
};

function pickFirst(field: string | string[] | undefined): string | undefined {
  if (!field) return undefined;
  return Array.isArray(field) ? field[0] : field;
}

/** Surfaces the first field-level error from an alternative-domain API rejection as a toast. */
export function showApiError(error: unknown, fallback = "Something went wrong") {
  const data = isApiError(error) ? (error.response?.data as FieldErrorBody | undefined) : undefined;
  const message =
    pickFirst(data?.website) ??
    pickFirst(data?.old_website) ??
    pickFirst(data?.new_website) ??
    pickFirst(data?.alternative_domains) ??
    data?.message ??
    data?.detail ??
    fallback;
  ToastService.showError(message);
}

/** Joins counterpart names into a human-readable list ("A", "A and B", "A, B, and C"). */
export function formatCounterpartNames(counterparts: CrossTeamCounterpart[]): string {
  const names = counterparts.map((counterpart) => counterpart.name);
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(", ")}, and ${names[names.length - 1]}`;
}

/** Domain-only, validated value for the BE (no protocol, no path/www). Null when invalid. */
export function normalizeURLForBackend(rawUrl: string): string | null {
  const trimmed = rawUrl.trim();
  if (!trimmed) return null;
  const withProtocol = addUrlProtocol(trimmed);
  if (!validateURL(withProtocol)) return null;
  return removeHttpPrefix(getBaseUrl(withProtocol));
}

export type RemoveTarget = { domain: string; counterparts?: CrossTeamCounterpart[] };

/** The saved alternative at `index` plus any cross-team counterparts, for a removal confirmation. */
export function findRemoveTarget(
  alternatives: string[],
  crossTeamLastMatchingDomains: CrossTeamLastMatchingDomain[],
  index: number
): RemoveTarget | null {
  const target = alternatives[index];
  if (!target) return null;
  const crossTeamMatch = crossTeamLastMatchingDomains.find((entry) => entry.domain === target);
  return { domain: target, counterparts: crossTeamMatch?.counterparts };
}

export function getCompanyTypeOptions(
  types: IPagedResponse<ICompanyType>
): GroupBase<ICompanyTypeSelectOption>[] {
  const allTypes =
    types?.results?.map((type) => ({
      ...type,
      value: type?.id || "",
      label: type?.title || "",
    })) || [];

  return [{ options: allTypes }];
}

export function getPriorityOptions(
  priorities: IPagedResponse<ICompanyPriority>
): IPrioritySelectOption[] {
  return (
    priorities?.results.map((priority) => ({
      ...priority,
      value: priority?.id || "",
      label: priority?.title || "",
    })) || []
  );
}

export interface IGetStageOptionsProps {
  company?: ICompany;
  companyStages: IPagedResponse<ICompanyStage>;
  dealStages: IPagedResponse<DealStage>;
  types: IPagedResponse<ICompanyType>;
  currentType: ICompanyTypeSelectOption | undefined;
  noStageLabel?: string;
}

export function getStageOptions({
  company,
  companyStages,
  dealStages,
  types,
  currentType,
  noStageLabel = DEFAULT_NO_STAGE_LABEL,
}: IGetStageOptionsProps): GroupBase<IPipelineStageSelectOption>[] {
  if (!companyStages) return [];

  const stageOptions: GroupBase<IPipelineStageSelectOption>[] = [
    { label: noStageLabel, options: [{ value: "", label: noStageLabel, status: null }] },
  ];

  const activeDealStageId = company?.active_deal?.stage?.id ?? null;

  if (activeDealStageId) {
    const dealStageOptions = dealStages
      ? dealStages?.results.map((stage) => ({
          id: stage?.id || "",
          color: stage?.color || "",
          value: stage?.id || "",
          label: stage?.title || "",
          status: stage?.status || "",
          is_default: false,
        }))
      : [];

    stageOptions.push({
      label: "Deal Stages",
      options: dealStageOptions as IPipelineStageSelectOption[],
    });
  } else {
    const type = currentType ?? getDefaultItem(types?.results || []);

    const companyStageOptions = companyStages
      ? companyStages?.results
          .filter((stage) => stage.group === type?.group)
          .map((stage) => ({
            id: stage?.id || "",
            color: stage?.color || "",
            value: stage?.id || "",
            label: stage?.title || "",
            status: stage?.status || "",
            is_default: stage?.is_default || false,
          }))
      : [];

    stageOptions.push({
      label: "Company Stages",
      options: companyStageOptions as IPipelineStageSelectOption[],
    });
  }

  return stageOptions;
}
